/**
 * Documentation Categories Configuration
 *
 * Defines the categories for grouping documentation by feature area and use case.
 */

import type { LucideIcon } from 'lucide-react';
import {
  Bot,
  Shield,
  Link2,
  Wrench,
  Database,
  Plug,
  Server,
  Store,
  MessageSquare,
  Zap,
  Users,
  RefreshCw,
  Lock,
  DollarSign,
} from 'lucide-react';

export interface DocCategory {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  basePath: string;
}

/**
 * Feature area categories - organized by platform features
 */
export const featureCategories: DocCategory[] = [
  {
    id: 'agents',
    title: 'Agents',
    description: 'Create and manage AI agents',
    icon: Bot,
    basePath: 'agents',
  },
  {
    id: 'admin',
    title: 'Admin',
    description: 'Organization and platform administration',
    icon: Shield,
    basePath: 'admin',
  },
  {
    id: 'integrations',
    title: 'Integrations',
    description: 'Connect external services',
    icon: Link2,
    basePath: 'integrations',
  },
  {
    id: 'tools',
    title: 'Tools',
    description: 'MCP tools reference',
    icon: Wrench,
    basePath: 'tools',
  },
  {
    id: 'data',
    title: 'Data',
    description: 'Data browser and exploration',
    icon: Database,
    basePath: 'data',
  },
  {
    id: 'connectors',
    title: 'Connectors',
    description: 'Custom API connectors',
    icon: Plug,
    basePath: 'connectors',
  },
  {
    id: 'environments',
    title: 'Environments',
    description: 'Environment management',
    icon: Server,
    basePath: 'environments',
  },
  {
    id: 'marketplace',
    title: 'Marketplace',
    description: 'Template marketplace',
    icon: Store,
    basePath: 'marketplace',
  },
];

/**
 * Use case categories - organized by tutorials and workflows
 */
export const useCaseCategories: DocCategory[] = [
  {
    id: 'chatbot-rag',
    title: 'Chatbot & RAG',
    description: 'Conversational agents and knowledge bases',
    icon: MessageSquare,
    basePath: 'agents/create/use-cases/chatbot-rag',
  },
  {
    id: 'automation-tools',
    title: 'Automation',
    description: 'Webhooks and scheduled workflows',
    icon: Zap,
    basePath: 'agents/create/use-cases/automation-tools',
  },
  {
    id: 'multi-agent',
    title: 'Multi-Agent',
    description: 'Coordinated agent systems',
    icon: Users,
    basePath: 'agents/create/use-cases/multi-agent',
  },
  {
    id: 'data-sync',
    title: 'Data Sync',
    description: 'Data synchronization patterns',
    icon: RefreshCw,
    basePath: 'agents/create/use-cases/data-sync',
  },
  {
    id: 'enterprise-security',
    title: 'Enterprise Security',
    description: 'Compliance and security automation',
    icon: Lock,
    basePath: 'agents/create/use-cases/enterprise-security',
  },
  {
    id: 'finops',
    title: 'FinOps',
    description: 'Cloud cost optimization',
    icon: DollarSign,
    basePath: 'agents/create/use-cases/finops',
  },
];
